/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import type { RefObject } from 'preact';
import { FlattenedDataState } from '../UNSAFE_Collection';
import { Keys } from '../utils/UNSAFE_keys';
import { ListProps } from '../UNSAFE_ListView/List.types';
import { ViewportConfig } from '../hooks/UNSAFE_useViewportIntersect';

export const LIST_ITEM_SELECTOR = '[data-oj-list-item]';

/**
 * Helper function to only include keys of leaf items.
 */
export const excludeGroup = <K, D>(data: FlattenedDataState<K, D>, keys: Keys<K>) => {
  if (!keys.all) {
    const groups = data.data
      .filter((dataMetadata) => {
        return !dataMetadata.metadata.isLeaf;
      })
      .map((dataMetadata) => {
        return dataMetadata.metadata.key;
      });
    const leafOnly = Array.from(keys.keys.values()).filter((key: K) => {
      return !groups.includes(key);
    });
    return { ...keys, keys: new Set(leafOnly) };
  }
  return keys;
};

/**
 * A helper function to make sure specified elem is visible in the specified container
 */
export const scrollToVisible = (elem: Element, scroller: Element, offset: number) => {
  if (elem && scroller) {
    if ((elem as any).scrollIntoViewIfNeeded) {
      // for Safari, we'll need the non-standard scrollIntoViewIfNeeded
      (elem as any).scrollIntoViewIfNeeded();
    } else {
      elem.scrollIntoView({ block: 'nearest' });
    }
    if (offset > 0) {
      const scrollerBounds = scroller.getBoundingClientRect();
      const elemBounds = elem.getBoundingClientRect();
      const diff = scrollerBounds.top + offset - elemBounds.top;
      if (diff > 0) {
        scroller.scrollTop = scroller.scrollTop - diff;
      }
    }
  }
};

/**
 * Helper method to determine whether the selection mode is multiple or multipleToggle
 */
export const isMultipleSelection = (selectionMode: ListProps<any, any>['selectionMode']) => {
  return selectionMode === 'multiple' || selectionMode === 'multipleToggle';
};

/**
 * Helper method to get the viewport config
 */
export const getViewportConfig = (
  defaultScrollerRef: RefObject<HTMLElement>,
  config?: ViewportConfig
) => {
  return config ?? { scrollerRef: defaultScrollerRef };
};
